import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  MenuItem,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import { fetchSuppliers } from '../../api/suppliers';
import { Supplier } from '../../types/utility.types';

interface SupplierDropdownProps {
  value: string; // Supplier ID
  onChange: (supplierId: string, supplierName: string) => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  meterType?: 'MPAN' | 'MPRN';
  disabled?: boolean;
}

const SupplierDropdown: React.FC<SupplierDropdownProps> = ({
  value,
  onChange,
  label = "Supplier",
  required = false,
  error = false,
  helperText = "",
  meterType,
  disabled = false
}) => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      console.log('SupplierDropdown: Loading suppliers...');
      const data = await fetchSuppliers();
      console.log('SupplierDropdown: Raw suppliers data:', data);
      
      // Filter suppliers based on meter type if provided
      let filteredSuppliers = data.filter(supplier => supplier.isActive);
      console.log('SupplierDropdown: Active suppliers:', filteredSuppliers);
      
      if (meterType) {
        const serviceType = meterType === 'MPAN' ? 'Electricity' : 'Gas';
        console.log('SupplierDropdown: Filtering by service type:', serviceType);
        filteredSuppliers = filteredSuppliers.filter(supplier => 
          supplier.serviceTypes.includes(serviceType as any)
        );
        console.log('SupplierDropdown: Filtered suppliers:', filteredSuppliers);
      }
      
      setSuppliers(filteredSuppliers);
      setLoadError(null);
      console.log('SupplierDropdown: Final suppliers set:', filteredSuppliers);
    } catch (err) {
      console.error('Failed to load suppliers:', err);
      setLoadError('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  }, [meterType]);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const supplierId = event.target.value;
    const supplier = suppliers.find(s => s._id === supplierId);
    onChange(supplierId, supplier?.name || '');
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading suppliers...
        </Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <TextField
        fullWidth
        label={label}
        value=""
        error
        helperText={loadError}
        disabled
      />
    );
  }

  return (
    <TextField
      select
      fullWidth
      label={label}
      value={value}
      onChange={handleChange}
      required={required}
      error={error}
      helperText={helperText}
      disabled={disabled}
    >
      <MenuItem value="">
        <em>Select a supplier</em>
      </MenuItem>
      {suppliers.map((supplier) => (
        <MenuItem key={supplier._id} value={supplier._id}>
          {supplier.name}
        </MenuItem>
      ))}
    </TextField>
  );
};

export default SupplierDropdown;
